import { z } from "zod";
import { emailSchema, validatePhoneForCountry, getPhoneValidationExample, getPhoneLength } from "./common";
import { PROFILE_TAGS } from "../constants/valisdation";
import { DOCUMENT_TYPES } from "../../views/Pages/Auth/constant";

// Comprehensive creator registration schema
export const creatorRegistrationSchema = z.object({
  accountType: z
    .string()
    .min(1, "Please select an account type")
    .refine(
      (val) => val === "monthly" || val === "ppv",
      "Please select a valid account type"
    ),

  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]+$/, "First name can only contain letters and spaces"),

  middleName: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 2,
      "Middle name must be at least 2 characters if provided"
    )
    .refine(
      (val) => !val || val.length <= 50,
      "Middle name must be less than 50 characters"
    )
    .refine(
      (val) => !val || /^[a-zA-Z\s]+$/.test(val),
      "Middle name can only contain letters and spaces"
    ),

  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]+$/, "Last name can only contain letters and spaces"),

  email: emailSchema,

  username: z
    .string()
    .min(1, "Username is required")
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9]+$/, "Username can only contain letters and numbers")
    .refine(
      (val) => (val.match(/[a-zA-Z]/g) || []).length >= 3,
      "Username must contain at least 3 alphabetic characters"
    ),

  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/\d/, "Password must contain at least one number")
    .regex(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character"),

  confirmPassword: z
    .string()
    .min(1, "Please confirm your password"),

  dateOfBirth: z
    .string()
    .min(1, "Date of birth is required")
    .refine(
      (date) => {
        const birthDate = new Date(date);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        return age >= 18;
      },
      "You must be at least 18 years old"
    ),

  phoneCountry: z
    .string()
    .optional()
    .default("+1"),

  phoneCountryCode: z
    .string()
    .optional()
    .default("US"), // ISO country code for validation

  phoneNumber: z
    .string()
    .optional(),

  address: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 5,
      "Address must be at least 5 characters if provided"
    )
    .refine(
      (val) => !val || val.length <= 200,
      "Address must be less than 200 characters"
    ),

  country: z
    .string()
    .min(1, "Country is required")
    .default("US"),

  state: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 2,
      "State must be at least 2 characters if provided"
    )
    .refine(
      (val) => !val || val.length <= 50,
      "State must be less than 50 characters"
    ),

  terms: z
    .boolean()
    .refine((val) => val === true, "You must agree to the terms and conditions"),

  referralId: z
    .string()
    .nullish()
    .transform((val) => val === null || val === undefined ? undefined : val),

  // File upload validations
  profileImage: z
    .any()
    .nullable()
    .refine(
      (file) => !file || (file && file.size && file.size <= 10 * 1024 * 1024),
      "Profile image must be less than 10MB"
    )
    .refine(
      (file) => !file || (file && file.type && ["image/jpeg", "image/jpg", "image/png"].includes(file.type)),
      "Profile image must be a JPG or PNG file"
    ),

  idFront: z
    .any()
    .nullable()
    .refine(
      (file) => !file || (file && file.size && file.size <= 10 * 1024 * 1024),
      "Front ID image must be less than 10MB"
    )
    .refine(
      (file) => !file || (file && file.type && ["image/jpeg", "image/jpg", "image/png"].includes(file.type)),
      "Front ID image must be a JPG or PNG file"
    ),

  idBack: z
    .any()
    .nullable()
    .refine(
      (file) => !file || (file && file.size && file.size <= 10 * 1024 * 1024),
      "Back ID image must be less than 10MB"
    )
    .refine(
      (file) => !file || (file && file.type && ["image/jpeg", "image/jpg", "image/png"].includes(file.type)),
      "Back ID image must be a JPG or PNG file"
    ),



  // Selected tags validation
  selectedTags: z
    .array(z.string())
    .min(PROFILE_TAGS.MIN_TAGS, "At least one tag is required")
    .max(PROFILE_TAGS.MAX_TAGS, `You can select a maximum of ${PROFILE_TAGS.MAX_TAGS} tags`)
    .refine(
      (tags) => tags.every(tag => tag.trim().length > 0),
      "All tags must be valid and not empty"
    ),

  // Document type validation
  documentType: z
    .string()
    .min(1, "Document type is required")
    .refine(
      (val) => val === "id" || val === "passport",
      "Please select a valid document type"
    ),
}).refine(
  (data) => data.password === data.confirmPassword,
  {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  }
).superRefine((data, ctx) => {
  // Helper: treat File or any object with size as valid (works on all devices/browsers)
  const hasFile = (file) =>
    file &&
    typeof file === 'object' &&
    (file instanceof File
      ? file.size > 0
      : typeof file.size === 'number' && file.size > 0);
  const hasProfileImage = hasFile(data.profileImage);
  const hasIdFront = hasFile(data.idFront);
  const hasIdBack = hasFile(data.idBack);

  // Validate profile image (required for all)
  if (!hasProfileImage) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Profile image (selfie) is required",
      path: ["profileImage"],
    });
  }

  // Validate document uploads based on type
  if (data.documentType === DOCUMENT_TYPES.ID) {
    if (!hasIdFront) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Front of ID is required",
        path: ["idFront"],
      });
    }
    if (!hasIdBack) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Back of ID is required",
        path: ["idBack"],
      });
    }
  } else if (data.documentType === DOCUMENT_TYPES.PASSPORT) {
    if (!hasIdFront) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passport image is required",
        path: ["idFront"],
      });
    }
  }
}).superRefine((data, ctx) => {
  // Validate phone number against the country code (optional for creators)
  const countryCode = data.phoneCountryCode || 'US';
  const phoneNumber = data.phoneNumber ? data.phoneNumber.trim() : '';

  if (!phoneNumber) {
    return;
  }

  const isValid = validatePhoneForCountry(phoneNumber, countryCode, false);

  if (!isValid) {
    const example = getPhoneValidationExample(countryCode);
    const requiredLength = getPhoneLength(countryCode);
    const currentLength = phoneNumber.replace(/\D/g, '').length;

    let message = `Invalid phone number for ${countryCode}.`;

    if (currentLength === 0) {
      message = `Phone number format is incorrect. Expected ${requiredLength} digits. Example: ${example}`;
    } else if (currentLength < requiredLength) {
      message = `Phone number is too short. You entered ${currentLength} digit${currentLength !== 1 ? 's' : ''}, but ${requiredLength} digit${requiredLength !== 1 ? 's are' : ' is'} required. Example: ${example}`;
    } else if (currentLength > requiredLength) {
      message = `Phone number is too long. You entered ${currentLength} digit${currentLength !== 1 ? 's' : ''}, but only ${requiredLength} digit${requiredLength !== 1 ? 's are' : ' is'} required. Example: ${example}`;
    } else {
      message = `Invalid phone number format. Please use the correct format: ${example}`;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["phoneNumber"],
    });
  }
});
